import { Form } from 'antd';
import Text from 'components/Text';
import { IApiSplitTicketItem } from 'features/ticketList/services/types/ticket';
import styled from 'styled-components';
import StaffRow from './StaffRow';
import Box from 'components/Box';
import { momentTimezone } from 'utils/time';
import Add from 'assets/Icons/add';
import colorTheme from 'constants/color';
import { formatCurrency } from 'utils/formatCurrency';
import { CategoryType } from './autoBalanceHelpers';
import { useRef } from 'react';
import { IStaffItemData } from 'services/shop/types/staff';

interface TableSplitTicketProps {
  details: any;
  handleAddStaffAutoBalance: (defaultValue: IApiSplitTicketItem) => void;
  handleRemoveStaffAutoBalance: (index: number) => void;
  handleFieldChange: (index: number, category: CategoryType, value: number) => void;
  handleFocus: (index: number, category: CategoryType) => void;
  handleBlur: (category: CategoryType) => void;
  setVisibleChooseStaff: (visible: boolean) => void;
  filterStaff: (list: IStaffItemData[]) => IStaffItemData[];
}

const TableSplitTicket = ({
  details,
  handleAddStaffAutoBalance,
  handleRemoveStaffAutoBalance,
  handleFieldChange,
  handleFocus,
  handleBlur,
  setVisibleChooseStaff,
  filterStaff,
}: TableSplitTicketProps) => {
  const addRef = useRef<(defaultValue?: IApiSplitTicketItem) => void>(() => undefined);

  return (
    <Form.List name={'staffs'}>
      {(fields, { add }) => {
        // Override add function để sử dụng auto-balance
        const addWithAutoBalance = (defaultValue?: IApiSplitTicketItem) => {
          if (defaultValue) {
            handleAddStaffAutoBalance(defaultValue);
          } else {
            add();
          }
        };

        // Override remove function để sử dụng auto-balance
        const removeWithAutoBalance = (index: number) => {
          handleRemoveStaffAutoBalance(index);
        };

        return (
          <>
            <Title>
              <Box display='flex' gap='2' alignItems='center'>
                <div className='ticket-number'>
                  <span>#{details?.ticketNumber}</span>
                </div>
                <Box display='flex' flexDirection='column' gap='1'>
                  <Text variant='H6' color='text_4_v2' textAlign='left' style={{ fontWeight: 500 }}>
                    {momentTimezone(details?.startTime).format('DD/MM/YYYY')}{' '}
                  </Text>
                  <Text variant='H6' color='text_4_v2' textAlign='left' style={{ fontWeight: 500 }}>
                    {momentTimezone(details?.startTime).format('hh:mm A')} -{' '}
                    {momentTimezone(details?.endTime).format('hh:mm A')}
                  </Text>
                </Box>
              </Box>

              <button
                className='add-button'
                type='button'
                onClick={() => {
                  setVisibleChooseStaff(true);
                  addRef.current = addWithAutoBalance;
                }}
              >
                <Add fill={colorTheme.v2_secondary_3} width={32} height={32} />
                <Text variant='H4' color='v2_secondary_3' className='medium-text'>
                  Add New
                </Text>
              </button>
            </Title>
            <div className='table-wrapper'>
              <TableStyled className='table-container'>
                <thead className='table-thead'>
                  <tr>
                    <th className='service-th'>
                      <div className='container-box'>
                        <span>SERVICE NAME</span>
                      </div>
                    </th>
                    <th className='staff-th'>
                      <div className='container-box'>
                        <span>STAFF</span>
                      </div>
                    </th>
                    <th>
                      <div className='container-box'>
                        <span>PRICE</span>
                      </div>
                    </th>
                    <th>
                      <div className='container-box'>
                        <span>TIP</span>
                      </div>
                    </th>
                    <th>
                      <div className='container-box'>
                        <span>DISC.</span>
                      </div>
                    </th>
                    <th>
                      <div className='container-box'>
                        <span>SUPPLY</span>
                      </div>
                    </th>
                    <th className='action'>
                      <div className='container-box'>
                        <span>ACTION</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className='table-tbody'>
                  {fields.map((o, i) => (
                    <Form.Item noStyle name={o.name} key={o.key}>
                      <StaffRow
                        name={o.name}
                        remove={() => removeWithAutoBalance(i)}
                        disabledRemove={fields.length === 1}
                        // disableDiscount={!details?.discountTicket}
                        disableSupplyFee={!details.supplyFee}
                        onFieldChange={(category, value) => handleFieldChange(i, category, value)}
                        onFocus={(category) => handleFocus(i, category)}
                        onBlur={handleBlur}
                        filterStaff={filterStaff}
                      />
                    </Form.Item>
                  ))}
                </tbody>
                <tfoot>
                  <tr className='thead-total-row'>
                    <th>
                      <div className='container-box'>
                        <Text variant='H1'>Total</Text>
                      </div>
                    </th>
                    <th>
                      <div className='container-box' />
                    </th>
                    <th>
                      <div className='container-box center'>
                        <Text variant='H1'>{formatCurrency(details.subTotal)}</Text>
                      </div>
                    </th>
                    <th>
                      <div className='container-box center'>
                        <Text variant='H1'>{formatCurrency(details.tip)}</Text>
                      </div>
                    </th>
                    <th>
                      <div className='container-box center'>
                        <Text variant='H1' color='v2_primary_bg_6'>{formatCurrency(details.discountItem)}</Text>
                      </div>
                    </th>
                    <th>
                      <div className='container-box center'>
                        <Text variant='H1'>{formatCurrency(details.supplyFee)}</Text>
                      </div>
                    </th>
                    <th className='action'>
                      <div className='container-box' />
                    </th>
                  </tr>
                </tfoot>
              </TableStyled>
            </div>
          </>
        );
      }}
    </Form.List>
  );
};
export default TableSplitTicket;

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;

  .ticket-number {
    display: flex;
    padding: 4px 16px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 4px;
    background: #232f3e;

    span {
      color: #fff;
      font-family: Poppins;
      font-size: 36px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }
  }

  .add-button {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 8px;
  }
`;

const TableStyled = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-family: Poppins;
  &.table-container {
    .table-thead {
      tr {
        th {
          padding: 8px 12px;
          border-bottom: 1px solid ${colorTheme.v2_border_2};
          &.no-style {
            background: transparent;
          }
          span {
            font-size: 16px;
            color: ${colorTheme.text_3_v2};
          }
        }
        &.thead-total-row {
          font-size: 1.5rem;
          .container-box {
            text-align: right;
            justify-content: flex-end;
            padding-right: 12px;
          }
          th {
            height: 48px;
            padding: 0;
          }
        }
      }
    }
    th,
    td {
      position: relative;
      z-index: 1;
      min-width: 150px;
      &.staff-info {
        min-width: 120px;
      }
      &.action {
        min-width: 48px;
      }
      .container-box {
        background: transparent;
        display: flex;
        align-items: center;
        &.staff-col {
          justify-content: start;
        }

        &.center {
          justify-content: center;
        }
      }
      &.action {
        padding: 8px !important;
      }
    }

    th {
      height: 28px;
    }

    td {
      height: 64px;
      border-bottom: 1px solid ${colorTheme.v2_border_2};
    }

    .service-th {
      min-width: 240px;
    }

    .staff-th {
      min-width: 190px;
    }

    .table-tbody {
      tr {
        td {
          width: 100%;
          padding: 16px 12px;
          &.no-style {
            background: transparent;
          }
          &.staff-info {
            min-width: 100px;
            .CONTENT_2 {
              font-size: 1.2rem;
            }
          }
          .ant-input {
            height: 54px !important;
            text-align: center;
            font-size: 1.5rem;
            font-weight: 500;
            font-family: Poppins;
            &:disabled {
              // color: #1D2129;
              background: #fff;
            }
          }
        }
      }

      .btn-delete {
        height: 48px;
        width: 48px;
        border-radius: 8px;
        color: #fff;
        span {
          font-size: 1.5rem;
        }
      }
    }
  }
`;
